import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { NgbPaginationModule, NgbTooltipModule, NgbAlertModule, NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../services/auth.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { CreateOrganizationModalComponent } from '../create-organization-modal/create-organization-modal.component';
import { EditOrganizationModalComponent } from '../edit-organization-modal/edit-organization-modal.component';
import { environment } from '../../../environments/environment';

// Organization interface based on API response
export interface OrganizationDetails {
  id: string;
  organizationName: string;
  organizationType: string;
  officePhone: string;
  mobilePhone: string;
  email: string;
  lineId?: string;
  website?: string;
  taxId: string;
  headOfficeAddress: string;
  projectName?: string;
  taxSoi?: string;
  taxRoad?: string;
  taxSubdistrict: string;
  taxDistrict: string;
  taxProvince: string;
  postalCode?: string;
  adminRights: string;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  membershipStatus?: 'YES' | 'NO';
  memberCount?: number;
}

// API Response interface
export interface OrganizationApiResponse {
  data: OrganizationDetails[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Organization filters
export interface OrganizationFilters {
  searchTerm: string;
  organizationType: string;
  membershipStatus: string;
  status: 'all' | 'active' | 'inactive';
}

@Component({
  selector: 'app-organization-management',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    NgbPaginationModule, 
    NgbTooltipModule, 
    NgbAlertModule,
    NgbDropdownModule,
    StaffHeaderComponent, 
    StaffNavComponent
  ],
  templateUrl: './organization-management.component.html',
  styleUrl: './organization-management.component.scss'
})
export class OrganizationManagementComponent implements OnInit, OnDestroy {
  currentUser: AuthUser | null = null;
  isMobileMenuOpen = false;
  private userSubscription: Subscription | undefined;
  
  organizations: OrganizationDetails[] = [];
  filteredOrganizations: OrganizationDetails[] = [];
  isLoading = false;
  error: string | null = null;
  showMockDataWarning = false;

  // Filter properties
  filters: OrganizationFilters = {
    searchTerm: '',
    organizationType: '',
    membershipStatus: '',
    status: 'all'
  };

  // Pagination
  page = 1;
  pageSize = 10;
  readonly pageSizeOptions = [10, 20, 50, 100];
  collectionSize = 0;
  
  // Sorting
  sortField = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  private subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private modalService: NgbModal,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    console.log('🔄 OrganizationManagementComponent ngOnInit เริ่มต้น');
    
    // Subscribe to auth user first
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      console.log('👤 Current user updated:', user);
      this.currentUser = user;
      this.cdr.detectChanges();
    });

    // Use NgZone to ensure operations run inside Angular zone
    this.ngZone.run(() => {
      setTimeout(() => {
        console.log('⏰ เริ่มโหลดข้อมูลหลังจาก timeout (inside NgZone)');
        this.loadOrganizations();
      }, 100);
    });
  }

  ngOnDestroy(): void {
    console.log('🗑️ OrganizationManagementComponent ngOnDestroy');
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    this.subscription.unsubscribe();
  }

  onLogout(): void {
    this.authService.logout();
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  onNavigate(route: string): void {
    this.closeMobileMenu();
    
    switch (route) {
      case 'users':
        this.router.navigate(['/staff/user-management']);
        break;
      case 'organizations':
        // Already on organization management page
        break;
      case 'courses':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'reports':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'settings':
        this.router.navigate(['/staff/dashboard']);
        break;
      default:
        this.router.navigate(['/staff/dashboard']);
        break;
    }
  }

  /**
   * Load all organizations from API
   */
  loadOrganizations(): void {
    console.log('🚀 เริ่มโหลดข้อมูลหน่วยงานจาก API...');
    
    this.isLoading = true;
    this.error = null;
    this.showMockDataWarning = false;
    
    // Force initial change detection
    this.cdr.detectChanges();

    // API call to fetch organizations
    const apiUrl = `${environment.apiUrl}/companies?page=${this.page}&limit=${this.pageSize}`;
    console.log('📡 Fetching from:', apiUrl);
    
    this.http.get<OrganizationApiResponse>(apiUrl).subscribe({
      next: (response) => {
        console.log('='.repeat(80));
        console.log('API RESPONSE - SUCCESS');
        console.log('='.repeat(80));
        console.log('Response:', response);
        console.log('Total organizations:', response.total);
        console.log('Current page:', response.page);
        console.log('Total pages:', response.totalPages);
        console.log('='.repeat(80));
        
        this.ngZone.run(() => {
          // Map API data and add membershipStatus from adminRights
          this.organizations = response.data.map(org => ({
            ...org,
            membershipStatus: org.adminRights === 'yes' ? 'YES' as const : 'NO' as const
          }));
          
          this.collectionSize = response.total;
          this.applyFilters();
          this.isLoading = false;
          this.cdr.detectChanges();
          
          console.log('✅ โหลดข้อมูลสำเร็จ:', this.organizations.length, 'รายการ');
          console.log('✅ Final state - organizations:', this.organizations.length, 'filtered:', this.filteredOrganizations.length);
        });
      },
      error: (error) => {
        console.log('='.repeat(80));
        console.log('API RESPONSE - ERROR');
        console.log('='.repeat(80));
        console.error('Error:', error);
        console.log('Error Status:', error.status);
        console.log('Error Message:', error.message);
        if (error.error) {
          console.log('Error Details:', error.error);
        }
        console.log('='.repeat(80));
        
        this.ngZone.run(() => {
          this.isLoading = false;
          this.error = 'เกิดข้อผิดพลาดในการโหลดข้อมูลหน่วยงาน: ' + (error.error?.message || error.message);
          this.organizations = [];
          this.filteredOrganizations = [];
          this.collectionSize = 0;
          this.cdr.detectChanges();
        });
      }
    });
  }

  /**
   * Apply filters to organization list
   */
  applyFilters(): void {
    console.log('🔍 เริ่ม applyFilters - organizations:', this.organizations.length, 'filters:', this.filters);
    
    if (!this.organizations || this.organizations.length === 0) {
      console.log('⚠️ ไม่มีข้อมูลหน่วยงานสำหรับการกรอง');
      this.filteredOrganizations = [];
      this.collectionSize = 0;
      this.page = 1;
      this.cdr.detectChanges();
      return;
    }

    try {
      this.filteredOrganizations = this.organizations.filter(org => {
        // Search term filter
        if (this.filters.searchTerm) {
          const searchLower = this.filters.searchTerm.toLowerCase();
          const matchesSearch = 
            org.organizationName.toLowerCase().includes(searchLower) ||
            org.organizationType.toLowerCase().includes(searchLower);
          if (!matchesSearch) return false;
        }

        // Organization type filter
        if (this.filters.organizationType && this.filters.organizationType !== '') {
          if (org.organizationType !== this.filters.organizationType) return false;
        }

        // Membership status filter
        if (this.filters.membershipStatus && this.filters.membershipStatus !== '') {
          if (org.membershipStatus !== this.filters.membershipStatus) return false;
        }

        // Status filter
        if (this.filters.status !== 'all') {
          const isActive = this.filters.status === 'active';
          if (org.isActive !== isActive) return false;
        }

        return true;
      });

      this.collectionSize = this.filteredOrganizations.length;
      this.page = 1; // Reset to first page when filters change
      
      console.log('✅ applyFilters สำเร็จ - filteredOrganizations:', this.filteredOrganizations.length, 'collectionSize:', this.collectionSize);
      this.cdr.detectChanges();
      
    } catch (error) {
      console.error('❌ เกิดข้อผิดพลาดใน applyFilters:', error);
      this.filteredOrganizations = [...this.organizations];
      this.collectionSize = this.organizations.length;
      this.page = 1;
      this.cdr.detectChanges();
    }
  }

  /**
   * Get current page organizations
   */
  getCurrentPageOrganizations(): OrganizationDetails[] {
    console.log('📖 getCurrentPageOrganizations called - page:', this.page, 'pageSize:', this.pageSize, 'filteredOrganizations:', this.filteredOrganizations.length);
    
    if (!this.filteredOrganizations || this.filteredOrganizations.length === 0) {
      console.log('⚠️ No filtered organizations available');
      return [];
    }
    
    const startIndex = (this.page - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    const currentPageOrganizations = this.filteredOrganizations.slice(startIndex, endIndex);
    
    console.log('📄 Page calculation - startIndex:', startIndex, 'endIndex:', endIndex, 'result:', currentPageOrganizations.length, 'organizations');
    
    return currentPageOrganizations;
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filters = {
      searchTerm: '',
      organizationType: '',
      membershipStatus: '',
      status: 'all'
    };
    this.applyFilters();
  }

  /**
   * Sort organizations by field
   */
  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }

    this.filteredOrganizations.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (field) {
        case 'organizationName':
          aValue = a.organizationName || '';
          bValue = b.organizationName || '';
          break;
        case 'organizationType':
          aValue = a.organizationType || '';
          bValue = b.organizationType || '';
          break;
        case 'memberCount':
          aValue = a.memberCount || 0;
          bValue = b.memberCount || 0;
          break;
        case 'membershipStatus':
          aValue = a.membershipStatus || '';
          bValue = b.membershipStatus || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  /**
   * Get sort icon for column
   */
  getSortIcon(field: string): string {
    if (this.sortField !== field) return '';
    return this.sortDirection === 'asc' ? '▲' : '▼';
  }

  /**
   * Change page size
   */
  changePageSize(size: number): void {
    this.pageSize = size;
    this.page = 1;
    this.loadOrganizations(); // Reload data with new page size
  }

  /**
   * Toggle organization status
   */
  toggleOrganizationStatus(organization: OrganizationDetails): void {
    const newStatus = !organization.isActive;
    const confirmation = confirm(
      `คุณต้องการ${newStatus ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}หน่วยงาน ${organization.organizationName} หรือไม่?`
    );

    if (confirmation) {
      organization.isActive = newStatus;
      organization.updatedAt = new Date().toISOString().split('T')[0];
      // TODO: Call API to update status
    }
  }

  /**
   * Edit organization information
   */
  editOrganization(organization: OrganizationDetails): void {
    try {
      console.log('🔧 Edit organization:', organization);
      console.log('Opening edit organization modal with ID:', organization.id);
      
      const modalRef = this.modalService.open(EditOrganizationModalComponent, {
        size: 'lg',
        backdrop: 'static',
        keyboard: false,
        windowClass: 'edit-organization-modal',
        scrollable: true,
        centered: true
      });
      
      // Pass company ID to the modal
      modalRef.componentInstance.companyId = organization.id;
      
      modalRef.result.then(
        (result: any) => {
          console.log('Edit organization modal closed with result:', result);
          
          if (result && result.success) {
            console.log('Organization updated successfully:', result.organizationData);
            
            // Reload organizations from API after successful update
            this.loadOrganizations();
            // Alert is already shown in the modal
          }
        },
        (dismissed: any) => {
          console.log('Edit organization modal dismissed:', dismissed);
        }
      ).catch((error: any) => {
        console.error('Error with edit organization modal:', error);
      });
      
    } catch (error: any) {
      console.error('Error opening edit organization modal:', error);
      this.error = 'เกิดข้อผิดพลาดในการเปิดหน้าต่างแก้ไขหน่วยงาน';
      
      setTimeout(() => {
        this.error = null;
        this.cdr.detectChanges();
      }, 5000);
    }
  }

  /**
   * Delete organization with confirmation
   */
  deleteOrganization(organization: OrganizationDetails): void {
    console.log('🗑️ Delete organization:', organization);
    
    const confirmation = confirm(
      `⚠️ คุณต้องการลบหน่วยงาน "${organization.organizationName}" หรือไม่?\n\n` +
      `การดำเนินการนี้ไม่สามารถยกเลิกได้!`
    );

    if (!confirmation) {
      return;
    }

    // Double confirmation for safety
    const doubleConfirmation = confirm(
      `❗ ยืนยันการลบหน่วยงานอีกครั้ง?\n\n` +
      `หน่วยงาน: ${organization.organizationName}\n` +
      `ประเภท: ${organization.organizationType}\n` +
      `จำนวนสมาชิก: ${organization.memberCount} คน\n\n` +
      `พิมพ์ "DELETE" เพื่อยืนยัน`
    );

    if (doubleConfirmation) {
      const index = this.organizations.findIndex(o => o.id === organization.id);
      if (index > -1) {
        this.organizations.splice(index, 1);
        this.applyFilters();
        alert(`✅ ลบหน่วยงาน "${organization.organizationName}" เรียบร้อยแล้ว`);
      }
    }
  }

  /**
   * Create new organization
   */
  createNewOrganization(): void {
    try {
      console.log('Opening create organization modal');
      
      const modalRef = this.modalService.open(CreateOrganizationModalComponent, {
        size: 'lg',
        backdrop: 'static',
        keyboard: false,
        windowClass: 'create-organization-modal',
        scrollable: true,
        centered: true
      });
      
      modalRef.result.then(
        (result: any) => {
          console.log('Create organization modal closed with result:', result);
          
          if (result && result.success) {
            console.log('Organization created successfully:', result.organizationData);
            
            // Reload organizations from API after successful creation
            this.loadOrganizations();
            // Alert is already shown in the modal
          }
        },
        (dismissed: any) => {
          console.log('Create organization modal dismissed:', dismissed);
        }
      ).catch((error: any) => {
        console.error('Error with create organization modal:', error);
      });
      
    } catch (error: any) {
      console.error('Error opening create organization modal:', error);
      this.error = 'เกิดข้อผิดพลาดในการเปิดหน้าต่างเพิ่มหน่วยงาน';
      
      setTimeout(() => {
        this.error = null;
        this.cdr.detectChanges();
      }, 5000);
    }
  }

  /**
   * Export organizations to CSV
   */
  exportToCSV(): void {
    const csvData = this.filteredOrganizations.map(org => ({
      'ลำดับ': this.filteredOrganizations.indexOf(org) + 1,
      'ชื่อหน่วยงาน': org.organizationName,
      'ประเภทหน่วยงาน': org.organizationType,
      'จำนวนสมาชิก': org.memberCount,
      'สมาชิกสมทบ': org.membershipStatus,
      'สถานะ': org.isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน',
      'วันที่สร้าง': org.createdAt,
      'วันที่อัปเดต': org.updatedAt
    }));

    // Convert to CSV
    const headers = Object.keys(csvData[0] || {});
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => headers.map(header => `"${row[header as keyof typeof row]}"`).join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `organizations_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Get status class for styling
   */
  getStatusClass(organization: OrganizationDetails): string {
    return organization.isActive ? 'status-active' : 'status-inactive';
  }

  /**
   * Track by function for ngFor optimization
   */
  trackByOrganizationId(index: number, organization: OrganizationDetails): string {
    return organization.id;
  }

  /**
   * Math helper for template
   */
  min(a: number, b: number): number {
    return Math.min(a, b);
  }
}
